import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/layout/Header';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Calendar, MapPin, Eye, Image as ImageIcon, Plus } from 'lucide-react';
import { getImageUrl } from '@/utils/helpers';
import { format } from 'date-fns';
import { zhCN } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ExhibitionsPage = () => {
  const { user } = useAuth();
  const [exhibitions, setExhibitions] = useState([]);
  const [featuredExhibition, setFeaturedExhibition] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    fetchExhibitions();
  }, []);

  const fetchExhibitions = async () => {
    try {
      const res = await axios.get(`${API}/exhibitions`);
      const data = res.data || [];
      
      // 找出精选画展
      const featured = data.find(e => e.is_featured);
      if (featured) {
        setFeaturedExhibition(featured);
        setExhibitions(data.filter(e => e.id !== featured.id));
      } else {
        setExhibitions(data);
      }
    } catch (err) {
      console.error('Failed to fetch exhibitions:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const getLocalizedField = (item, field) => {
    const lang = i18n.language;
    if (lang === 'en' && item[`${field}_en`]) return item[`${field}_en`];
    return item[field] || '';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return format(new Date(dateStr), 'yyyy年M月d日', { locale: zhCN });
    } catch {
      return dateStr;
    }
  };

  if (isLoading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-amber-50 flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-2 border-amber-300 border-t-amber-600 rounded-full"></div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-amber-50" data-testid="exhibitions-page">
        {/* Hero Section - 精选画展 */}
        {featuredExhibition && (
          <section 
            className="relative h-[70vh] flex items-end cursor-pointer group"
            onClick={() => navigate(`/exhibitions/${featuredExhibition.id}`)}
          >
            <div className="absolute inset-0">
              <img loading="lazy"
                src={getImageUrl(featuredExhibition.cover_image) || 'https://images.unsplash.com/photo-1577720643272-265f09367456?w=1920'}
                alt={featuredExhibition.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
            </div>
            
            <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pb-16">
              <Badge className="bg-white/20 text-white backdrop-blur-sm mb-4">
                精选画展
              </Badge>
              <h1 className="text-4xl md:text-6xl font-light text-white mb-4">
                {getLocalizedField(featuredExhibition, 'title')}
              </h1>
              <p className="text-lg text-white/70 max-w-2xl mb-6 line-clamp-2">
                {getLocalizedField(featuredExhibition, 'description')}
              </p>
              <div className="flex flex-wrap items-center gap-6 text-white/60 text-sm mb-6">
                {featuredExhibition.organizer && (
                  <span>主办：{featuredExhibition.organizer}</span>
                )}
                {featuredExhibition.location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {featuredExhibition.location}
                  </span>
                )}
                {featuredExhibition.start_date && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {formatDate(featuredExhibition.start_date)}
                    {featuredExhibition.end_date && ` - ${formatDate(featuredExhibition.end_date)}`}
                  </span>
                )}
              </div>
              <Button className="bg-amber-50 text-amber-800 hover:bg-amber-100 rounded-full px-8 py-6">
                进入画展
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </section>
        )}

        {/* 画展列表 */}
        <section className="py-20 px-6 bg-amber-50/50">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-16">
              <div className="text-center flex-1">
                <h2 className="text-4xl font-light text-amber-900 mb-4">画展专题</h2>
                <p className="text-amber-700/70">探索精彩纷呈的艺术展览</p>
              </div>
              {user && (
                <Button 
                  onClick={() => navigate('/exhibitions/create')}
                  className="rounded-full bg-amber-600 hover:bg-amber-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  创建展览
                </Button>
              )}
            </div>

            {exhibitions.length === 0 && !featuredExhibition ? (
              <div className="text-center py-20">
                <ImageIcon className="w-16 h-16 text-amber-300 mx-auto mb-4" />
                <p className="text-amber-600 mb-6">暂无画展</p>
                {user ? (
                  <Button onClick={() => navigate('/exhibitions/create')} className="rounded-full bg-amber-600 hover:bg-amber-700">
                    <Plus className="w-4 h-4 mr-2" />
                    创建第一个展览
                  </Button>
                ) : (
                  <Button onClick={() => navigate('/login')} variant="outline" className="rounded-full border-amber-600 text-amber-700 hover:bg-amber-100">
                    登录后创建展览
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {exhibitions.map((exhibition) => (
                  <div
                    key={exhibition.id}
                    className="group cursor-pointer"
                    onClick={() => navigate(`/exhibitions/${exhibition.id}`)}
                    data-testid={`exhibition-card-${exhibition.id}`}
                  >
                    {/* 封面图 */}
                    <div className="relative aspect-[4/3] rounded-2xl overflow-hidden mb-4">
                      <img loading="lazy"
                        src={getImageUrl(exhibition.cover_image) || 'https://images.unsplash.com/photo-1577720643272-265f09367456?w=800'}
                        alt={exhibition.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                      
                      {/* 作品预览 */}
                      {exhibition.preview_artworks?.length > 0 && (
                        <div className="absolute bottom-4 left-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          {exhibition.preview_artworks.slice(0, 3).map((artwork, idx) => (
                            <div key={idx} className="w-12 h-12 rounded-lg overflow-hidden border-2 border-white/50">
                              <img loading="lazy"
                                src={getImageUrl(artwork.image_url)} 
                                alt="" 
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ))}
                          {exhibition.artwork_count > 3 && (
                            <div className="w-12 h-12 rounded-lg bg-black/50 backdrop-blur-sm flex items-center justify-center text-white text-xs border-2 border-white/50">
                              +{exhibition.artwork_count - 3}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* 信息 */}
                    <div>
                      <h3 className="text-xl font-medium text-amber-900 mb-2 group-hover:text-amber-600 transition-colors">
                        {getLocalizedField(exhibition, 'title')}
                      </h3>
                      <p className="text-amber-700/70 text-sm line-clamp-2 mb-3">
                        {getLocalizedField(exhibition, 'description')}
                      </p>
                      <div className="flex items-center justify-between text-sm text-amber-500">
                        <div className="flex items-center gap-4">
                          {exhibition.start_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {formatDate(exhibition.start_date)}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="flex items-center gap-1">
                            <ImageIcon className="w-4 h-4" />
                            {exhibition.artwork_count || 0}
                          </span>
                          <span className="flex items-center gap-1">
                            <Eye className="w-4 h-4" />
                            {exhibition.view_count || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default ExhibitionsPage;
